<template>
  <div class="layout-preview-wrap">
    <el-button size="mini" icon="el-icon-view" @click="openBrowse">浏览</el-button>
    <span class="layout-preview-follow" :class="{ 'is-empty': !followLabel }">{{ followLabel || '跟随学校' }}</span>

    <el-dialog
      title="内容分屏预览"
      :visible.sync="browseVisible"
      append-to-body
      :close-on-click-modal="true"
      custom-class="layout-browse-dialog"
      width="92%"
      top="3vh"
      @closed="onBrowseClosed"
    >
      <div class="browse-toolbar">
        <span class="browse-hint">当前 {{ modeLabel }}，一页 {{ cellCount }} 条；超过则分页轮播（示意 {{ pageIndex + 1 }}/{{ pageCount }}）</span>
        <el-button size="mini" type="primary" icon="el-icon-full-screen" @click="toggleFullscreen">
          {{ isNativeFullscreen ? "退出全屏" : "全屏展示" }}
        </el-button>    
      </div>
      <div class="browse-stage" ref="stageEl">
        <div v-if="isNativeFullscreen" class="browse-fs-exit">
          <el-button size="mini" icon="el-icon-close" @click="toggleFullscreen">退出全屏</el-button>
        </div>
        <div class="browse-screen" ref="screenEl">
          <div class="browse-bar">
            <span>{{ schoolTitle }}</span>
            <span class="browse-clock">{{ pageCount > 1 ? (pageIndex + 1) + ' / ' + pageCount + '　' : '' }}16:30</span>
          </div>
          <div class="browse-grid" :class="'is-' + mode">
            <div
              v-for="(item, idx) in pageItems"
              :key="idx"
              class="browse-cell"
              :class="item ? ['kind-' + item.kind, { 'has-caption': !!item.caption }] : 'is-empty'"
            >
              <template v-if="item">
                <span class="browse-badge">{{ item.badge }}</span>
                <div v-if="item.kind === 'text'" class="browse-text">
                  <div class="browse-text-card">{{ item.caption }}</div>
                </div>
                <template v-else>
                  <div class="browse-media">
                    <img class="browse-cover" :src="item.cover" :alt="item.caption || item.badge" />
                  </div>
                  <div v-if="item.caption" class="browse-caption">{{ item.caption }}</div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "ViolationLayoutPreview",
  props: {
    /** 1 全屏 / 2 二分 / 4 四分 */
    mode: {
      type: Number,
      default: 1
    },
    followLabel: {
      type: String,
      default: ""
    },
    schoolTitle: {
      type: String,
      default: "放学大屏 · 内容轮播"
    }
  },
  data() {
    return {
      browseVisible: false,
      isNativeFullscreen: false,
      pageIndex: 0,
      pageTimer: null
    };
  },
  computed: {
    cellCount() {
      return this.mode === 2 || this.mode === 4 ? this.mode : 1;
    },
    modeLabel() {
      if (this.mode === 2) return "二分屏";
      if (this.mode === 4) return "四分屏";
      return "全屏";
    },
    /** 预览用本地示意图，不读学校真实内容 */
    samples() {
      // build:api 下 publicPath=/ui/，须带 BASE_URL；正式包 publicPath=/ 仍为 /static/...
      const base = process.env.BASE_URL || "/";
      const img = (name) => `${base}static/dismissal-preview/${name}`;
      return [
        {
          kind: "image",
          badge: "图片",
          caption: "明日全校大扫除，请各班按时参加",
          cover: img("campus.jpg")
        },
        {
          kind: "violation",
          badge: "违停",
          caption: "违停｜浙A·12345｜学生小明｜东门家长等候区\n补充说明：占用消防通道，请及时挪车",
          cover: img("car.jpg")
        },
        {
          kind: "text",
          badge: "文字",
          caption: "请家长在等候区接送，勿占用车道。雨天请携带雨具，接送车辆请按指引停放。"
        },
        {
          kind: "image",
          badge: "图片",
          caption: "",
          cover: img("activity.jpg")
        },
        {
          kind: "image",
          badge: "图片",
          caption: "请家长在等候区接送，勿占用车道。雨天请携带雨具，接送车辆请按指引停放，请勿在校门口临时停车或鸣笛，以免影响放学通行。",
          cover: img("waiting.jpg")
        },
        {
          kind: "violation",
          badge: "违停",
          caption: "违停｜浙B·88888｜学生小红｜西门斑马线",
          cover: img("car.jpg")
        },
        {
          kind: "text",
          badge: "文字",
          caption: "本周值日检查重点：红领巾佩戴、课间文明休息、放学有序排队。"
        },
        {
          kind: "image",
          badge: "图片",
          caption: "本周食堂菜单已更新，请关注班级群通知",
          cover: img("campus.jpg")
        },
        {
          kind: "image",
          badge: "图片",
          caption: "",
          cover: img("activity.jpg")
        }
      ];
    },
    pageCount() {
      const n = this.samples.length;
      const size = this.cellCount;
      return Math.max(1, Math.ceil(n / size));
    },
    pageItems() {
      const size = this.cellCount;
      const start = this.pageIndex * size;
      const items = this.samples.slice(start, start + size);
      while (items.length < size) items.push(null);
      return items;
    }
  },
  watch: {
    cellCount() {
      this.pageIndex = 0;
      if (this.browseVisible) this.startPageTimer();
    }
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", this.syncFullscreenState);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.syncFullscreenState);
    document.removeEventListener("webkitfullscreenchange", this.syncFullscreenState);
    this.stopPageTimer();
    this.exitNativeFullscreen();
  },
  methods: {
    openBrowse() {
      this.browseVisible = true;
      this.pageIndex = 0;
      this.startPageTimer();
    },
    onBrowseClosed() {
      this.stopPageTimer();
      this.exitNativeFullscreen();
    },
    startPageTimer() {
      this.stopPageTimer();
      if (this.pageCount <= 1) return;
      this.pageTimer = setInterval(() => {
        this.pageIndex = (this.pageIndex + 1) % this.pageCount;
      }, 4000);
    },
    stopPageTimer() {
      if (this.pageTimer) {
        clearInterval(this.pageTimer);
        this.pageTimer = null;
      }
    },
    syncFullscreenState() {
      const el = document.fullscreenElement || document.webkitFullscreenElement;
      this.isNativeFullscreen = !!(el && (el === this.$refs.stageEl || el === this.$refs.screenEl));
    },
    toggleFullscreen() {
      if (this.isNativeFullscreen) {
        this.exitNativeFullscreen();
        return;
      }
      const el = this.$refs.stageEl;
      if (!el) return;
      const req = el.requestFullscreen || el.webkitRequestFullscreen;
      if (req) {
        Promise.resolve(req.call(el)).catch(() => {
          this.$message.warning("当前浏览器无法进入全屏，已使用大窗口预览");
        });
      } else {
        this.$message.warning("当前浏览器不支持全屏");
      }
    },
    exitNativeFullscreen() {
      const el = document.fullscreenElement || document.webkitFullscreenElement;
      if (!el) return;
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) exit.call(document);
    }
  }
};
</script>

<style scoped>
.layout-preview-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.layout-preview-follow {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}
.layout-preview-follow.is-empty {
  visibility: hidden;
}
.browse-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.browse-hint {
  font-size: 13px;
  color: #909399;
}
.browse-stage {
  position: relative;
  height: calc(92vh - 140px);
  min-height: 420px;
  background: #020617;
}
.browse-fs-exit {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
}
.browse-screen {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #0f172a;
  color: #fff;
}
.browse-bar {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  font-size: 22px;
  font-weight: 700;
  background: rgba(30, 41, 59, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.browse-clock {
  font-size: 18px;
  font-weight: 600;
  color: #cbd5e1;
}
.browse-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
}
.browse-grid.is-1 {
  grid-template-columns: 1fr;
}
.browse-grid.is-2 {
  grid-template-columns: 1fr 1fr;
}
.browse-grid.is-4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}
.browse-cell {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
}
.browse-cell.kind-text {
  background:
    radial-gradient(80% 70% at 50% 40%, rgba(30, 58, 95, 0.55), transparent 70%),
    linear-gradient(180deg, #0b1220 0%, #020617 100%);
}
.browse-cell.is-empty {
  background:
    linear-gradient(145deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95));
  border: 1px dashed rgba(100, 116, 139, 0.45);
}
.browse-media,
.browse-cover {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.browse-cover {
  object-fit: cover;
  display: block;
}
.browse-text {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px 14px 20px;
  box-sizing: border-box;
  background: transparent;
}
.browse-text-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  padding: 0;
  box-sizing: border-box;
  font-size: 15px;
  line-height: 1.55;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
  color: #f8fafc;
  text-shadow: 0 1px 10px rgba(2, 6, 23, 0.4);
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
}
.browse-grid.is-1 .browse-text-card {
  font-size: 22px;
}
.browse-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  max-height: 4.8em;
  padding: 10px 14px 12px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.45;
  color: #f8fafc;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.72) 28%, rgba(15, 23, 42, 0.92) 100%);
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
}
.browse-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.82);
  color: #e2e8f0;
}
.kind-violation .browse-badge {
  color: #fbbf24;
}
.kind-text .browse-badge {
  color: #7dd3fc;
}
.browse-info {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 16px 18px 14px;
  background: linear-gradient(180deg, transparent, rgba(15, 23, 42, 0.88));
  color: #e2e8f0;
}
.browse-info strong {
  display: block;
  font-size: 20px;
  font-weight: 700;
}
.browse-info span {
  display: block;
  margin-top: 4px;
  font-size: 15px;
  color: #cbd5e1;
}
.browse-plate {
  font-size: 30px;
  font-weight: 700;
  color: #fbbf24;
  letter-spacing: 1px;
  line-height: 1.2;
}
</style>

<style>
.layout-browse-dialog {
  margin-bottom: 0;
}
.layout-browse-dialog .el-dialog__body {
  padding-top: 8px;
  padding-bottom: 16px;
}
.layout-browse-dialog .browse-cell.kind-text {
  background:
    radial-gradient(80% 70% at 50% 40%, rgba(30, 58, 95, 0.55), transparent 70%),
    linear-gradient(180deg, #0b1220 0%, #020617 100%) !important;
}
.layout-browse-dialog .browse-text {
  position: absolute !important;
  inset: 0 !important;
  z-index: 1;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 36px 14px 20px;
  box-sizing: border-box;
  background: transparent;
}
.layout-browse-dialog .browse-text::before {
  content: none !important;
  display: none !important;
}
.layout-browse-dialog .browse-text-card {
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  padding: 0;
  box-sizing: border-box;
  font-size: 15px;
  line-height: 1.55;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
  color: #f8fafc;
  text-shadow: 0 1px 10px rgba(2, 6, 23, 0.4);
  background: none !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
}
.layout-browse-dialog .browse-grid.is-1 .browse-text-card {
  font-size: 22px;
}
.browse-stage:fullscreen,
.browse-stage:-webkit-full-screen {
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
}
.browse-stage:fullscreen .browse-cell.kind-text,
.browse-stage:-webkit-full-screen .browse-cell.kind-text {
  background:
    radial-gradient(80% 70% at 50% 40%, rgba(30, 58, 95, 0.55), transparent 70%),
    linear-gradient(180deg, #0b1220 0%, #020617 100%) !important;
}
.browse-stage:fullscreen .browse-text,
.browse-stage:-webkit-full-screen .browse-text {
  position: absolute !important;
  inset: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
</style>
